/* eslint-disable jsx-a11y/anchor-is-valid */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Grid } from '@ui-propulsion/react-components';
import { readingTime } from 'reading-time-estimator';
import PostHeader from '../postHeader/postHeader';
import PostBody from '../postBody/postBody';
import styles from './blogPost.module.scss';
import TableOfContents from '../tableOfContents/tableOfContents';
import Breadcumbs from '../breadcrumbs/breadcrumbs';
import GlossaryHubPrequal from '../glossaryHubPrequal/glossaryHubPrequal';
import LcResources from '../lcResources/lcResources';
import SubNavBar from '../subNavBar/subNavBar';
import HelpCenterSearch from '../helpCenterSearch/helpCenterSearch';

function BlogPost({
  breadcumbs = [],
  singlePost = {
    url: '',
    postContent: '',
    ctaButton: {},
    title: '',
    thumbnail: null,
    postDate: '',
    reviewer: '',
    author: '',
    factChecker: '',
    keyTakeAways: {},
  },
  prequal = {},
  lcResources = [],
  tableOfContents = [],
  showTableOfContents = true,
}) {
  const [showContinueReading, setShowContinueReading] = useState(false);
  const [showSubNavBar, setShowSubNavBar] = useState(false);
  const stats = readingTime(singlePost.postContent);
  const timeDuration = stats.text;

  const searchBox = {
    placeholder: 'Search',
    onEnter: (query) => {
      window.location.assign(`/resource-center/search?query=${query}`);
    },
  };

  useEffect(() => {
    const onScroll = () => {
      if (window.pageYOffset >= 300) {
        setShowSubNavBar(true);
      } else {
        setShowSubNavBar(false);
      }
    };
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const postHeadingData = {
    postTitle: singlePost.title,
    postDate: singlePost.postDate,
    author: singlePost.author ?? null,
    reviewer: singlePost.reviewer ?? null,
    factChecker: singlePost.factChecker ?? null,
    thumbnail: singlePost.thumbnail ?? null,
    keyTakeAways: singlePost.keyTakeAways,
    readingTime: timeDuration,
  };

  const postBodyData = {
    slug: singlePost.url,
    postContent: singlePost.postContent,
    ctaButton: singlePost.ctaButton,
  };

  return (
    <div
      className={styles.blogPost}
      itemScope
      itemType="https://schema.org/Article"
    >
      <div className={styles.container}>
        <div className={styles.section}>
          {showTableOfContents && tableOfContents.length > 0 && (
            <div className={styles.sidebar}>
              <TableOfContents
                items={tableOfContents}
                onClick={() => {
                  setShowContinueReading(false);
                }}
              />
            </div>
          )}
          <div className={styles.postContent}>
            {showTableOfContents &&
              tableOfContents.length > 0 &&
              showSubNavBar && (
                <div className={styles.subNavBar}>
                  <SubNavBar
                    items={[
                      { label: 'Table of Contents', link: '#' },
                      ...tableOfContents,
                    ]}
                    maxItemsPerRow={1}
                    offset={-72}
                  />
                </div>
              )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7} className={styles.breadcumbsContainer}>
                <div className={styles.breadcrumbs}>
                  <Breadcumbs links={breadcumbs} />
                </div>
              </Grid>
              <Grid item xs={12} sm={5}>
                <HelpCenterSearch {...searchBox} />
              </Grid>
            </Grid>
            <PostHeader {...postHeadingData} />
            <PostBody
              {...postBodyData}
              showContinueReading={showContinueReading}
              contentType="Blog"
            />
            {prequal && (
              <div className={styles.prequalWrapper}>
                <GlossaryHubPrequal {...prequal} />
              </div>
            )}
            {lcResources.length > 0 && (
              <div
                className={`${styles.section} ${styles.lcResourcesContainer}`}
              >
                <LcResources
                  title="You May Also Like"
                  lcResources={lcResources}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default BlogPost;

BlogPost.propTypes = {
  breadcumbs: PropTypes.array,
  singlePost: PropTypes.shape({
    url: PropTypes.string,
    postContent: PropTypes.string,
    ctaButton: PropTypes.object,
    title: PropTypes.string,
    thumbnail: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    postDate: PropTypes.string,
    reviewer: PropTypes.string,
    author: PropTypes.string,
    factChecker: PropTypes.string,
    keyTakeAways: PropTypes.object,
  }),
  recommendedArticles: PropTypes.array,
  articlesAndResources: PropTypes.array,
  prequal: PropTypes.object,
  backUrl: PropTypes.string,
  lcResources: PropTypes.array,
  tableOfContents: PropTypes.array,
  showTableOfContents: PropTypes.bool,
};
